import { CellContext, ColumnDef } from '@tanstack/react-table'
import { ColumnDefWithFilters, EnrichedTeamMembership } from '../types'
import { useMemo } from 'react'
import { usePageInfo } from 'app/frontend/base/config/queries'
import { useGuild } from 'app/frontend/base/config/context/guild'
import { useAudit } from '../context'
import { genericSorting } from 'app/frontend/base/config/column-definitions/util'
import { getDungeons } from 'app/frontend/base/services/dungeonValues'
import { computeRankById, createRankColumn } from '../utils'
import { keyBy } from 'lodash'
import AuditFilter, { useAuditFilterState } from '../components/AuditFilter'
import Select from 'app/frontend/base/components/Select'
import { characterOption } from 'app/frontend/base/components/ApplicationCharacterCard'
import KeystoneRunCard from 'app/frontend/base/components/KeystoneRunCard'

export const minimumLevelOptions = Array.from({ length: 18 }, (_, index) => ({
  label: `Level ${(index + 3).toString()}`,
  sortLabel: '',
  value: (index + 3).toString(),
}))

export const useMinimumLevelFilter = () => {
  const { value: minimumLevelParam, setValue: setMinimumLevel } =
    useAuditFilterState<string | undefined>('minimumLevel', undefined)
  const minimumLevel = minimumLevelParam ? Number(minimumLevelParam) : undefined

  const minimumLevelFilter = useMemo(() => {
    return (
      <AuditFilter key='minimum_level' label='Min keystone level'>
        <Select
          className='w-32'
          isClearable={true}
          setValue={setMinimumLevel}
          value={minimumLevelParam}
          placeholder='Any'
          options={minimumLevelOptions}
        />
      </AuditFilter>
    )
  }, [minimumLevelParam, setMinimumLevel])

  return {
    minimumLevelFilter,
    minimumLevel,
  }
}

export const useWeeklyDungeonTotalColumn = (
  header: string,
  subheader: string,
  minimumLevel?: number,
) => {
  const { guild } = useGuild()
  const {
    data: { currentPeriod, currentSeason },
  } = usePageInfo()
  const { visibleMembers, weeksAgo, showingRanks } = useAudit()

  const period = currentPeriod[guild?.realm.region ?? 'EU'] - weeksAgo
  const isPreseason =
    weeksAgo === 0 && (currentSeason?.first_period ?? 0) > period

  const sortedRunsByMember = useMemo(
    () =>
      new Map(
        visibleMembers.map((member) => [
          member.id,
          Object.entries(member.keystones?.[period] ?? {})
            .filter(([_, run]) => run.level >= (minimumLevel ?? 0))
            .map(([key, run]) => ({ ...run, key }))
            .sort((a, b) => b.level - a.level),
        ]),
      ),
    [minimumLevel, visibleMembers, period],
  )

  const totalAccessor = (member: EnrichedTeamMembership) => {
    if (isPreseason) {
      return Number(member.data?.['week_mythic_dungeons']) || 0
    }

    return sortedRunsByMember.get(member.id)?.length ?? 0
  }

  const rankById = useMemo(
    () => computeRankById(visibleMembers, totalAccessor),
    [visibleMembers, sortedRunsByMember, isPreseason],
  )

  const weeklyDungeonTotalColumn = useMemo(() => {
    return {
      id: 'weekly_dungeons_total',
      header,
      columns: [
        ...(showingRanks
          ? [createRankColumn(rankById, 'weekly_dungeons_total_rank', '')]
          : []),
        {
          id: 'weekly_dungeons_count',
          ...genericSorting<EnrichedTeamMembership>(
            'alphanumeric',
            subheader,
            true,
          ),
          meta: { className: 'border-r' },
          size: 80,
          accessorFn: totalAccessor,
          cell: (info: CellContext<EnrichedTeamMembership, unknown>) => (
            <span>{totalAccessor(info.row.original) || '0'}</span>
          ),
        },
      ],
    }
  }, [minimumLevel, sortedRunsByMember, showingRanks, rankById, isPreseason])

  return {
    sortedRunsByMember,
    totalAccessor,
    rankById,
    weeklyDungeonTotalColumn,
  }
}

export const useDungeonsWeeklyColumns = (): ColumnDefWithFilters => {
  const { weeksAgo, visibleMembers, showingRanks, activeView } = useAudit()
  const { value: graphTargetId, setValue: setGraphTargetId } =
    useAuditFilterState<string | undefined>('graphTarget', undefined)
  const graphTarget = useMemo(
    () =>
      graphTargetId
        ? visibleMembers.find((m) => m.id.toString() === graphTargetId)
        : undefined,
    [graphTargetId, visibleMembers],
  )
  const { minimumLevelFilter, minimumLevel } = useMinimumLevelFilter()
  const { guild } = useGuild()
  const {
    data: { currentPeriod, currentSeason, gameVersion },
  } = usePageInfo()

  const { sortedRunsByMember, rankById, weeklyDungeonTotalColumn } =
    useWeeklyDungeonTotalColumn('', 'Total', minimumLevel)

  const dungeons = useMemo(
    () => keyBy(getDungeons(gameVersion), 'id'),
    [gameVersion],
  )

  const period = currentPeriod[guild?.realm.region ?? 'EU'] - weeksAgo

  const maxRuns = useMemo(
    () =>
      visibleMembers.reduce((max, member) => {
        const runs = member.keystones?.[period]
        return Math.max(max, runs ? Object.keys(runs).length : 0)
      }, 8),
    [minimumLevel, visibleMembers, period],
  )

  const columns: ColumnDef<EnrichedTeamMembership>[] = useMemo(() => {
    return [
      weeklyDungeonTotalColumn,
      {
        id: 'weekly_dungeons',
        header: 'Keystone dungeons completed',
        meta: { className: 'border-l' },
        columns: Array.from({ length: maxRuns }, (_, index) => ({
          id: `keystone_run_${index}`,
          ...genericSorting<EnrichedTeamMembership>(
            'alphanumeric',
            `#${index + 1}`,
            true,
          ),
          accessorFn: (row: EnrichedTeamMembership) => {
            return sortedRunsByMember.get(row.id)?.[index]?.level ?? 0
          },
          size: 80,
          meta: { className: 'py-0.5' },
          cell: (info: CellContext<EnrichedTeamMembership, unknown>) => {
            const run = sortedRunsByMember.get(info.row.original.id)?.[index]
            if (!run) return null

            return <KeystoneRunCard run={run} dungeon={dungeons[run.dungeon]} />
          },
        })),
      },
    ]
  }, [maxRuns, minimumLevel, sortedRunsByMember, showingRanks, rankById])

  const filters = useMemo(() => {
    return [
      minimumLevelFilter,
      ...(activeView === 'graph'
        ? [
            <AuditFilter key='target' label='Graph data'>
              <Select
                className='w-48'
                isClearable={true}
                optionWidth='fit'
                value={graphTargetId}
                setValue={setGraphTargetId}
                placeholder='Entire team'
                options={visibleMembers.map((member) => ({
                  label: member.characterReference.name,
                  value: member.id.toString(),
                  customOptionFn: (isSelected: boolean) =>
                    characterOption(member.characterReference, isSelected),
                }))}
              />
            </AuditFilter>,
          ]
        : []),
    ]
  }, [minimumLevel, visibleMembers, graphTargetId, activeView])

  const graphData = useMemo(() => {
    const config = {
      total: {
        label: graphTarget ? graphTarget.characterReference.name : 'Total runs',
        color: graphTarget
          ? `var(--color-class-${graphTarget.characterReference.characterClass
              .toLowerCase()
              .replace(' ', '-')})`
          : 'var(--color-primary)',
      },
    }

    const firstPeriod = currentSeason?.first_period
    if (!firstPeriod) return { config, values: [] }
    const untilPeriod = currentPeriod[guild?.realm.region ?? 'EU']
    const weekCount = untilPeriod - firstPeriod + 1

    const totals = new Array<number>(weekCount).fill(0)

    const members = graphTarget ? [graphTarget] : visibleMembers

    for (const member of members) {
      if (!member.keystones) continue
      for (let i = 0; i < weekCount; i++) {
        const runs = member.keystones[firstPeriod + i]
        if (runs)
          totals[i] += Object.values(runs).filter(
            (run) => run.level >= (minimumLevel ?? 0),
          ).length
      }
    }

    return {
      config,
      values: totals.map((total, index) => ({
        label: (index + 1).toString(),
        total,
      })),
    }
  }, [
    graphTarget?.id,
    currentSeason,
    currentPeriod,
    visibleMembers,
    guild?.realm.region,
    minimumLevel,
  ])

  return {
    columns,
    filters,
    hasHistory: true,
    graphData,
    initialSortBy: [{ id: 'weekly_dungeons_count', desc: true }],
  }
}
